import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams, useNavigate, NavLink, Link } from "react-router-dom";
import { Header } from "./components/Header";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import { type } from "@testing-library/user-event/dist/type";

const API_URL = "/api";

export default function ViewTransactionList() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [transactionData, setTransactionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getTransactionDetails/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setTransactionData(data);
        setFormData(data);
        console.log("Data:", data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, []);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormData({ ...transactionData }); // reset the form
    setIsEditing(false);
  };

  function formatDateToDDMMYYYY(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const { _id, ...rest } = formData;
    if (!_id) {
      alert("Missing _id");
      return;
    }
    // Remove any fields with undefined values
    const cleanedData = {};
    for (const key in rest) {
      if (rest[key] !== undefined) {
        cleanedData[key] = rest[key];
      }
    }
    const updatePayload = {
      _id: _id, // Make sure it's a string
      update_data: cleanedData,
    };

    try {
      const response = await fetch(
        "http://localhost:8000/riverchain/techop/v1.1/updateCompanyLog",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        }
      );

      const resultText = await response.text();

      if (!response.ok) {
        console.error("❌ Update failed:", resultText);
        alert("Update failed:\n" + resultText);
      } else {
        toast.success("Company log updated successfully");
      }
    } catch (error) {
      console.error("🚨 Network error:", error);
      toast.danger("An error occurred while updating the profile.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <Header />
        </main>
      </div>

      <div className="container">
        <div className="row mt-4">
          <div className="col-md-12">
            <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
              Back to Transaction List
            </NavLink>

            <br />
            <br />
            <NavLink
              to={`/ViewTransactionList/${id}`}
              className="btn btn-dark ms-2"
            >
              Loan Details
            </NavLink>
            <NavLink
              to={`/Drawdowns/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Drawdowns
            </NavLink>
            <NavLink
              to={`/ApplicationChecklist/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Application Checklist
            </NavLink>
            <NavLink
              to={`/ScoreCard/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scorecard
            </NavLink>
            <NavLink
              to={`/TransactionSummary/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Transaction Summary
            </NavLink>
            <NavLink
              to={`/TermSheet/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Term Sheet
            </NavLink>
            <NavLink
              to={`/Payments/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Payments
            </NavLink>
            <NavLink
              to={`/RepaymentAndInterest/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Repayment & Interest(Term)
            </NavLink>

            <NavLink
              to={`/ScoringCriteriaTf/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scoring Criteria
            </NavLink>

            <NavLink
              to={`/ScoringGuide/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scoring Guide
            </NavLink>

            <NavLink
              to={`/InterestCalculations/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Interest Calculations
            </NavLink>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ marginTop: "15px" }}>Loan Transaction File</h3>
              <div>
                {isEditing && (
                  <button
                    className="btn btn-secondary me-2 btn-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    if (isEditing) {
                      setIsSubmitting(true);
                      await handleSubmit();
                      setIsSubmitting(false);
                    } else {
                      setFormData({ ...transactionData });
                      setIsEditing(true);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isEditing
                    ? isSubmitting
                      ? "Submitting..."
                      : "Submit"
                    : "Edit"}
                </button>
              </div>
            </div>

            {loading || !transactionData ? (
              <ShimmerTable row={5} col={5} />
            ) : (
              <div className="row">
                <div className="col-md-4">
                  <table className="table table-bordered">
                    <tbody>
                      {[
                        {
                          label: "Assessment Performed by",
                          key: "ASSESSMENTPERFORMEDBY",
                        },
                        { label: "Date", key: "DATE", type: "date" },
                        { label: "Review Performed by:", key: "" },
                        { label: "Date", key: "" },
                        { label: "Transaction ID: ", key: "TRANSACTIONID" },
                        {
                          label: "Lender",
                          key: "LENDER",
                        },
                        { label: "Status", key: "" },
                      ].map(({ label, key, type }) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td
                            style={{
                              textAlign: "right",
                              backgroundColor: "#fdf3d0",
                              width: "50%",
                            }}
                          >
                            {isEditing ? (
                              type === "date" ? (
                                <input
                                  type="date"
                                  value={
                                    formData?.[key]
                                      ? new Date(formData[key])
                                          .toISOString()
                                          .split("T")[0]
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={formData?.[key] ?? ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              )
                            ) : type === "date" ? (
                              transactionData?.[key] ? (
                                formatDateToDDMMYYYY(transactionData[key])
                              ) : (
                                ""
                              )
                            ) : (
                              transactionData?.[key]
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ backgroundColor: "#0074cc", color: "white" }}>
                    Transaction Details
                  </div>

                  <table className="table table-bordered">
                    <tbody>
                      {[
                        {
                          label: "Borrower",
                          key: "BORROWER",
                        },

                        { label: "Borrower Rating", key: "BORROWERRATING" },
                        { label: "Facility Limit (HK$):", key: "" },
                        {
                          label: "Availability Period (days/months):",
                          key: "",
                        },
                        { label: "Transaction ID: ", key: "TRANSACTIONID" },
                        {
                          label: "Product:",
                          key: "",
                        },
                        { label: "Repayment:", key: "" },
                        { label: "Type:", key: "TYPE" },
                        {
                          label: "Max. Term (months):",
                          key: "MAXTERM_MONTHS",
                        },
                        {
                          label: "Max. Term (days):",
                          key: "",
                        },
                        {
                          label: "Drawdown Limit %:",
                          key: "",
                        },
                        {
                          label: "Interest Rate: ",
                          key: "INTERESTRATE",
                        },
                        {
                          label: "Admin Fee:",
                          key: "ADMINFEE",
                        },
                        {
                          label: "Overdue Charge: ",
                          key: "ADMINFEE",
                        },
                        {
                          label: "Total Expected Revenue (HK$):",
                          key: "",
                        },
                        {
                          label: "Total Fees (HK$): ",
                          key: "",
                        },
                        {
                          label: "Total Interest (HK$):",
                          key: "",
                        },
                      ].map(({ label, key, type }) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td
                            style={{
                              textAlign: "right",
                              backgroundColor: "#fdf3d0",
                              width: "50%",
                            }}
                          >
                            {isEditing ? (
                              type === "date" ? (
                                <input
                                  type="date"
                                  value={
                                    formData?.[key]
                                      ? new Date(formData[key])
                                          .toISOString()
                                          .split("T")[0]
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={formData?.[key] ?? ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              )
                            ) : type === "date" ? (
                              transactionData?.[key] ? (
                                formatDateToDDMMYYYY(transactionData[key])
                              ) : (
                                ""
                              )
                            ) : key === "BORROWER" && transactionData?.[key] ? (
                              <NavLink
                                to={`/Details/${transactionData?.COMPANYID}`}
                                className="text-decoration-none"
                              >
                                {transactionData[key]}
                              </NavLink>
                            ) : (
                              transactionData?.[key]
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ backgroundColor: "#0074cc", color: "white" }}>
                    Security
                  </div>
                  <table className="table table-bordered">
                    <tbody>
                      {[
                        {
                          label: "Security Type:",
                          key: "SECURITYTYPE",
                        },
                      ].map(({ label, key, type }) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td
                            style={{
                              textAlign: "right",
                              backgroundColor: "#fdf3d0",
                              width: "50%",
                            }}
                          >
                            {isEditing ? (
                              type === "date" ? (
                                <input
                                  type="date"
                                  value={
                                    formData?.[key]
                                      ? new Date(formData[key])
                                          .toISOString()
                                          .split("T")[0]
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={formData?.[key] ?? ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              )
                            ) : type === "date" ? (
                              transactionData?.[key] ? (
                                formatDateToDDMMYYYY(transactionData[key])
                              ) : (
                                ""
                              )
                            ) : (
                              transactionData?.[key]
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ backgroundColor: "#0074cc", color: "white" }}>
                    Contract
                  </div>
                  <table className="table table-bordered">
                    <tbody>
                      {[
                        {
                          label: "Employer:",
                          key: "EMPLOYER",
                        },
                        {
                          label: "Main Contractor:",
                          key: "MAINCONTRACTOR",
                        },
                        {
                          label: "Sub Contractor (if any):",
                          key: "",
                        },
                        {
                          label: "Main Contract:",
                          key: "",
                        },
                        {
                          label: "Sub Contract:",
                          key: "",
                        },
                        {
                          label: "Contract Type:",
                          key: "",
                        },
                        {
                          label: "Construction Type:",
                          key: "",
                        },
                        {
                          label: "Project Status:",
                          key: "PROJECTSTATUS",
                        },
                        {
                          label: "Contract Amount (HK$):",
                          key: "",
                        },
                        {
                          label: "VOs (HK$):",
                          key: "",
                        },
                        {
                          label: "Total Contract Amount (HK$):",
                          key: "",
                        },
                        {
                          label: "Certified to Date (excl VO) (HK$):",
                          key: "CERTIFIEDTODATE",
                        },
                        {
                          label: "% Completed:",
                          key: "",
                        },
                        {
                          label: "Contract Start Date:",
                          key: "",
                        },
                        {
                          label: "Contract End Date:",
                          key: "",
                        },
                        {
                          label: "Contract Remaining Lifetime (Months):",
                          key: "",
                        },
                      ].map(({ label, key, type }) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td
                            style={{
                              textAlign: "right",
                              backgroundColor: "#fdf3d0",
                              width: "50%",
                            }}
                          >
                            {isEditing ? (
                              type === "date" ? (
                                <input
                                  type="date"
                                  value={
                                    formData?.[key]
                                      ? new Date(formData[key])
                                          .toISOString()
                                          .split("T")[0]
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={formData?.[key] ?? ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              )
                            ) : type === "date" ? (
                              transactionData?.[key] ? (
                                formatDateToDDMMYYYY(transactionData[key])
                              ) : (
                                ""
                              )
                            ) : (
                              transactionData?.[key]
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ backgroundColor: "#0074cc", color: "white" }}>
                    Contract
                  </div>
                  <table className="table table-bordered">
                    <tbody>
                      {[
                        {
                          label: "Employer:",
                          key: "EMPLOYER",
                        },
                        {
                          label: "Main Contractor:",
                          key: "MAINCONTRACTOR",
                        },
                        {
                          label: "Sub Contractor (if any):",
                          key: "",
                        },
                        {
                          label: "Main Contract:",
                          key: "",
                        },
                        {
                          label: "Sub Contract:",
                          key: "",
                        },
                        {
                          label: "Contract Type:",
                          key: "",
                        },
                        {
                          label: "Construction Type:",
                          key: "",
                        },
                        {
                          label: "Project Status:",
                          key: "PROJECTSTATUS",
                        },
                        {
                          label: "Contract Amount (HK$):",
                          key: "",
                        },
                        {
                          label: "VOs (HK$):",
                          key: "",
                        },
                        {
                          label: "Total Contract Amount (HK$):",
                          key: "",
                        },
                        {
                          label: "Certified to Date (excl VO) (HK$):",
                          key: "CERTIFIEDTODATE",
                        },
                        {
                          label: "% Completed:",
                          key: "",
                        },
                        {
                          label: "Contract Start Date:",
                          key: "",
                        },
                        {
                          label: "Contract End Date:",
                          key: "",
                        },
                        {
                          label: "Contract Remaining Lifetime (Months):",
                          key: "",
                        },
                      ].map(({ label, key, type }) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td
                            style={{
                              textAlign: "right",
                              backgroundColor: "#fdf3d0",
                              width: "50%",
                            }}
                          >
                            {isEditing ? (
                              type === "date" ? (
                                <input
                                  type="date"
                                  value={
                                    formData?.[key]
                                      ? new Date(formData[key])
                                          .toISOString()
                                          .split("T")[0]
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={formData?.[key] ?? ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              )
                            ) : type === "date" ? (
                              transactionData?.[key] ? (
                                formatDateToDDMMYYYY(transactionData[key])
                              ) : (
                                ""
                              )
                            ) : (
                              transactionData?.[key]
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ backgroundColor: "#0074cc", color: "white" }}>
                    Invoice Details (If any)
                  </div>
                  <table className="table table-bordered">
                    <tbody>
                      {[
                        {
                          label: "Invoice No.:",
                          key: "INVOICENO",
                        },
                        {
                          label: "Invoice Date:",
                          key: "INVOICEDATE",
                          type: "date",
                        },
                        {
                          label: "Invoice Value (HK$):",
                          key: "INVOICEVALUE_HK",
                        },
                        {
                          label: "Payment Cert Value (HK$):",
                          key: "",
                        },
                      ].map(({ label, key, type }) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td
                            style={{
                              textAlign: "right",
                              backgroundColor: "#fdf3d0",
                              width: "50%",
                            }}
                          >
                            {isEditing ? (
                              type === "date" ? (
                                <input
                                  type="date"
                                  value={
                                    formData?.[key]
                                      ? new Date(formData[key])
                                          .toISOString()
                                          .split("T")[0]
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={formData?.[key] ?? ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                />
                              )
                            ) : type === "date" ? (
                              transactionData?.[key] ? (
                                formatDateToDDMMYYYY(transactionData[key])
                              ) : (
                                ""
                              )
                            ) : (
                              transactionData?.[key]
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="col-md-8">
                  <div>Repayment Schedule </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
